import { supabase } from '../lib/supabase.js';
import { setState } from '../state/store.js';

// Company-wide, NOT filtered by selectedSiteId -- reads every site's
// quotation_amount plus every site's expense transactions in two plain
// selects (existing "auth read sites" / "auth read transactions" RLS
// policies already permit authenticated users to read across all sites),
// then aggregates client-side. No new RPC, no new table, no schema change
// beyond the one already-applied quotation_amount column.
//
// Quotation is a commercial reference value only -- it is never summed into
// income/expense/profit anywhere; this module only reads it back out.
export async function loadProjectHealth() {
  setState({ projectHealthLoading: true });

  const [sitesRes, txRes] = await Promise.all([
    supabase.from('sites').select('id, name, quotation_amount').order('name', { ascending: true }),
    supabase.from('transactions').select('site_id, amount').eq('type', 'expense')
  ]);

  if (sitesRes.error || txRes.error) {
    console.error('Failed to load project health', sitesRes.error || txRes.error);
    setState({ projectHealth: [], projectHealthLoading: false, projectHealthError: true });
    return;
  }

  const expenseBySite = {};
  for (const row of txRes.data || []) {
    expenseBySite[row.site_id] = (expenseBySite[row.site_id] || 0) + (Number(row.amount) || 0);
  }

  const projectHealth = (sitesRes.data || []).map((s) => {
    const quotation = Number(s.quotation_amount) || 0;
    const expense = expenseBySite[s.id] || 0;
    const remaining = quotation - expense;
    const pct = quotation > 0 ? (expense / quotation) * 100 : 0;
    return { id: s.id, name: s.name, quotation, expense, remaining, pct };
  });

  setState({ projectHealth, projectHealthLoading: false, projectHealthError: false });
}

// Company-wide Recent Transactions for the Dashboard -- deliberately a
// separate array from state.recentTransactions (which stays site-scoped for
// P&L/Materials/Labour/Reports, untouched by this). No site filter, so
// Dashboard content never changes when the selected site changes.
export async function loadDashboardTransactions() {
  setState({ dashboardTransactionsLoading: true });

  const { data, error } = await supabase
    .from('transactions')
    .select('id, site_id, entry_date, type, category, description, amount, payment_mode, created_at')
    .order('entry_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to load dashboard transactions', error);
    setState({ dashboardTransactions: [], dashboardTransactionsLoading: false, dashboardTransactionsError: true });
    return;
  }

  setState({ dashboardTransactions: data || [], dashboardTransactionsLoading: false, dashboardTransactionsError: false });
}

// Thresholds per spec: 0-60 Healthy, 60-80 Watch, 80-100 Attention, >100 Over Budget.
// A site with no quotation set (quotation <= 0) is a distinct state -- it is
// NOT "0% used" (that would misleadingly imply budget tracking is active),
// and it is never classified Healthy/Watch/etc, regardless of how much has
// been spent. Takes the whole health record (not just pct) so it can tell
// "no quotation" apart from "quotation set, nothing spent yet".
export function healthStatus(h) {
  if (!h.quotation || h.quotation <= 0) {
    return { pill: 'gray', emoji: '⚪', label: 'No Quotation', noQuotation: true };
  }
  if (h.pct > 100) return { pill: 'red', emoji: '🔴', label: 'Over Budget', noQuotation: false };
  if (h.pct > 80) return { pill: 'red', emoji: '🔴', label: 'Attention', noQuotation: false };
  if (h.pct > 60) return { pill: 'amber', emoji: '🟡', label: 'Watch', noQuotation: false };
  return { pill: 'green', emoji: '🟢', label: 'Healthy', noQuotation: false };
}
